import { useState } from 'react';
import Seo from '../components/Seo'
import { useFamilyStore } from '../stores/familyStore';
import Layout from '../components/layout/Layout';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { useEntitlements } from '../hooks/useEntitlements';
import MarketingHeader from '../components/marketing/MarketingHeader';
import { showConsentSettings } from '../utils/cookieConsentConfig';
import { useAction } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';

export default function Pricing() {
	const [yearly, setYearly] = useState(true);
	const [currency, setCurrency] = useState<'USD' | 'EUR'>('USD');
	const [actionLoading, setActionLoading] = useState<boolean>(false);
	const { family } = useFamilyStore();
	const user = useAuthStore(state => state.user);

	// Permissions – only parent A can manage subscription
	const { isParentA } = usePermissions();

	// Reactive plan data from Convex
	const entitlements = useEntitlements(family?.id);
	const activePlan = entitlements?.planId ?? null;
	const loadingPlan = !entitlements;

	// Check if family has Stripe subscription
	const hasStripeSubscription = !!(family && activePlan && activePlan !== 'basic' && activePlan !== 'trial');

	// Convex actions
	const createCheckoutSession = useAction(api.billing.actions.createCheckoutSession);
	const createBillingPortal = useAction(api.billing.actions.createBillingPortal);

	async function startCheckout(lookupKey: string) {
		if (!family) {
			alert('Log in and select a family to continue.');
			return;
		}
		try {
			setActionLoading(true);
			const result = await createCheckoutSession({
				familyId: family.id as Id<"families">,
				lookupKey,
				successUrl: `${window.location.origin}/?purchase=success`,
				cancelUrl: `${window.location.origin}/pricing?canceled=1`
			});
			if (result?.url) {
				window.location.href = result.url;
			}
		} catch (e: any) {
			alert(`Failed to start purchase: ${e?.message || 'try again later.'}`);
		} finally {
			setActionLoading(false);
		}
	}

	async function openPortal() {
		if (!family) {
			alert('Log in and select a family to continue.');
			return;
		}
		try {
			setActionLoading(true);
			const result = await createBillingPortal({
				familyId: family.id as Id<"families">,
				returnUrl: `${window.location.origin}/pricing`
			});
			if (result?.url) {
				window.location.href = result.url;
			}
		} catch (e: any) {
			alert(`Failed to open subscription portal: ${e?.message || 'check if you have an active subscription.'}`);
		} finally {
			setActionLoading(false);
		}
	}

	const monthlyPrices = {
		USD: { basic: '$0', pro: '$7.99' },
		EUR: { basic: '€0', pro: '€7.99' }
	} as const;

	const yearlyPrices = {
		USD: { basic: '$0', pro: '$79' },
		EUR: { basic: '€0', pro: '€79' }
	} as const;

	const founderPrice = {
		USD: '$99',
		EUR: '€99'
	} as const;

	const priceSuffix = yearly ? ' / year' : ' / month';

	const getPrice = (plan: 'basic' | 'pro') => {
		if (yearly) return yearlyPrices[currency][plan];
		return monthlyPrices[currency][plan];
	};

	const getLookupKey = (plan: 'pro' | 'founder') => {
		if (plan === 'founder') {
			const cur = currency.toLowerCase();
			return `founder_ltd_${cur}`;
		}
		const period = yearly ? 'yearly' : 'monthly';
		const cur = currency.toLowerCase();
		return `${plan}_${period}_${cur}`;
	};

	const content = (
		<div className="max-w-6xl mx-auto px-4 py-6 sm:py-10">
			<div className="flex flex-col items-center mb-5 space-y-3">
				<div className="inline-flex items-center bg-white border rounded-full shadow-sm px-1 py-1">
					<button
						onClick={() => setYearly(false)}
						className={`px-5 py-2 text-sm sm:text-base rounded-full transition ${!yearly ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-50'}`}
					>
						Monthly
					</button>
					<button
						onClick={() => setYearly(true)}
						className={`px-5 py-2 text-sm sm:text-base rounded-full transition ${yearly ? 'bg-blue-600 text-white shadow' : 'text-slate-700 hover:bg-slate-50'}`}
					>
						Yearly
					</button>
				</div>
				<div className="text-sm sm:text-base text-emerald-600 font-semibold animate-bounce">
					{yearly ? 'You are getting 2 months free!' : 'Get 2 months free with yearly billing!'}
				</div>
			</div>

			{family && (
				<p className="mb-3 text-base sm:text-lg text-slate-700 text-center">
					Your plan:{' '}
					<span className="font-semibold">
						{activePlan === 'max' ? 'Founder' : activePlan === 'trial' ? '14-Day Free Trial' : activePlan ?? '—'}
					</span>
				</p>
			)}

			{/* Plans grid */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-5">
				{/* Basic */}
				<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-slate-200 to-slate-300">
					<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-xl flex flex-col">
						<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>🌱</span> Basic (Solo)</h2>
						<div className="text-3xl font-bold mb-2">{getPrice('basic')}<span className="text-base font-medium text-slate-500">{priceSuffix}</span></div>
						<ul className="text-sm space-y-1 mb-4 text-slate-700">
							<li>calendar for unlimited children</li>
							<li>1 parent (Solo Mode)</li>
							<li>history lock (last 5 entries)</li>
							<li className="font-medium text-blue-600">includes 14-day free trial of Pro features</li>
						</ul>
						{!user ? (
							<a href="/auth/register" className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto text-center">Start Free Trial</a>
						) : activePlan === 'basic' ? (
							<button className="px-4 py-2 border rounded w-full mt-auto" disabled>Active</button>
						) : (
							<button className="px-4 py-2 border rounded w-full opacity-70 cursor-not-allowed mt-auto" disabled>Basic Plan</button>
						)}
					</div>
				</div>

				{/* Pro */}
				<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-blue-400 via-purple-400 to-indigo-400">
					<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-2xl flex flex-col">
						<div className="absolute -top-3 right-4 text-xs px-2 py-0.5 rounded-full bg-blue-600 text-white shadow">Most Popular</div>
						<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>🔹</span> Pro (Co-parenting)</h2>
						<div className="text-3xl font-bold mb-2 flex items-center justify-between">
							<div>{getPrice('pro')}<span className="text-base font-medium text-slate-500">{priceSuffix}</span></div>
							<div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 shadow-inner scale-90 origin-right">
								<button
									onClick={() => setCurrency('USD')}
									className={`px-2 py-0.5 text-xs rounded-md border transition ${currency === 'USD'
										? 'bg-white text-blue-700 border-blue-500 shadow font-semibold'
										: 'text-gray-500 border-transparent hover:bg-gray-200'
										}`}
								>
									$
								</button>
								<button
									onClick={() => setCurrency('EUR')}
									className={`px-2 py-0.5 text-xs rounded-md border transition ${currency === 'EUR'
										? 'bg-white text-blue-700 border-blue-500 shadow font-semibold'
										: 'text-gray-500 border-transparent hover:bg-gray-200'
										}`}
								>
									€
								</button>
							</div>
						</div>
						<ul className="text-sm space-y-1 mb-3 text-slate-700">
							<li>calendar for unlimited children</li>
							<li>2 parents + max 3 viewers</li>
							<li>events, notes, expenses</li>
							<li>reports (costs, care)</li>
							<li>180-day history</li>
							<li>unlimited calendar horizon</li>
							<li>no ads</li>
						</ul>
						<p className="text-xs text-slate-500 mb-5 italic">One pays, both use</p>
						{!user ? (
							<a
								href="/auth/register"
								className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto text-center"
							>
								Start Free Trial
							</a>
						) : activePlan === 'pro' ? (
							<button className="px-4 py-2 rounded w-full bg-green-600 text-white mt-auto">Active</button>
						) : (
							<button
								className="px-4 py-2 rounded w-full bg-blue-600 text-white hover:bg-blue-700 mt-auto disabled:opacity-60 disabled:cursor-not-allowed"
								disabled={loadingPlan || !family || !isParentA}
								title={!isParentA ? 'Only admin can change plan' : ''}
								onClick={() => {
									if (hasStripeSubscription) {
										openPortal();
									} else {
										startCheckout(getLookupKey('pro'));
									}
								}}
							>
								{hasStripeSubscription ? 'Switch to Pro (Portal)' : 'Choose Pro'}
							</button>
						)}
					</div>
				</div>

				{/* Founder (LTD) */}
				<div className="group relative rounded-2xl p-[2px] bg-gradient-to-r from-amber-400 via-pink-400 to-rose-400">
					<div className="rounded-2xl bg-white p-6 h-full transition-transform duration-200 group-hover:-translate-y-1 group-hover:shadow-2xl flex flex-col">
						<div className="absolute -top-3 right-4 text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white shadow">Best Value</div>
						<h2 className="text-xl sm:text-2xl font-bold mb-2 flex items-center gap-2"><span>⭐</span> Founder (VIP)</h2>
						<div className="text-3xl font-bold mb-2 flex items-center justify-between">
							<div>
								<span className="text-lg text-slate-400 line-through font-normal mr-2">
									{currency === 'USD' ? '$399' : '€399'}
								</span>
								{founderPrice[currency]}<span className="text-base font-medium text-slate-500"> one-time</span>
							</div>
							<div className="inline-flex items-center bg-gray-100 rounded-lg p-0.5 shadow-inner scale-90 origin-right">
								<button
									onClick={() => setCurrency('USD')}
									className={`px-2 py-0.5 text-xs rounded-md border transition ${currency === 'USD'
										? 'bg-white text-blue-700 border-blue-500 shadow font-semibold'
										: 'text-gray-500 border-transparent hover:bg-gray-200'
										}`}
								>
									$
								</button>
								<button
									onClick={() => setCurrency('EUR')}
									className={`px-2 py-0.5 text-xs rounded-md border transition ${currency === 'EUR'
										? 'bg-white text-blue-700 border-blue-500 shadow font-semibold'
										: 'text-gray-500 border-transparent hover:bg-gray-200'
										}`}
								>
									€
								</button>
							</div>
						</div>
						<ul className="text-sm space-y-1 mb-3 text-slate-700">
							<li>Everything in Pro</li>
							<li className="font-semibold text-amber-700">lifetime access</li>
						</ul>
						<p className="text-sm font-bold text-red-600 mb-3">Limited to 50 spots</p>
						{!user ? (
							<a
								href="/auth/register"
								className="px-4 py-2 rounded w-full bg-amber-600 text-white hover:bg-amber-700 mt-auto text-center"
							>
								Get Lifetime Access
							</a>
						) : activePlan === 'max' ? (
							<button className="px-4 py-2 rounded w-full bg-green-600 text-white mt-auto">Active</button>
						) : (
							<button
								className="px-4 py-2 rounded w-full bg-amber-600 text-white hover:bg-amber-700 mt-auto disabled:opacity-60 disabled:cursor-not-allowed"
								disabled={loadingPlan || !family || !isParentA}
								title={!isParentA ? 'Only admin can change plan' : ''}
								onClick={() => {
									startCheckout(getLookupKey('founder'));
								}}
							>
								Become Founder
							</button>
						)}
					</div>
				</div>
			</div>

			{
				family && isParentA && (
					<div className="mt-8 flex flex-col sm:flex-row gap-3">
						{hasStripeSubscription && (
							<button
								className="px-4 py-2 border rounded disabled:opacity-60"
								onClick={openPortal}
								disabled={actionLoading}
							>
								Manage subscription (Portal)
							</button>
						)}
						{activePlan && activePlan !== 'basic' && hasStripeSubscription && (
							<button
								className="px-4 py-2 border rounded text-red-600 border-red-300 hover:bg-red-50 disabled:opacity-60"
								onClick={openPortal}
								disabled={actionLoading}
								title="Cancel subscription (return to Basic)"
							>
								Cancel subscription (return to Basic)
							</button>
						)}
					</div>
				)
			}
			{
				family && !isParentA && (
					<p className="mt-6 text-center text-xs text-slate-500">Only admin can manage subscription</p>
				)
			}
		</div >
	);

	if (!user) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex flex-col">
				<Seo title="Pricing — 2homes.app" canonical="https://2homes.app/pricing" robots="index,follow" />
				<MarketingHeader />
				<main className="flex-grow">
					{content}
				</main>
				<footer className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8">
					<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
						<p className="text-slate-300">
							&copy; {new Date().getFullYear()} 2homes.app - Co-parenting calendar for parents and caregivers
						</p>
						<div className="mt-3 flex items-center justify-center gap-2 text-slate-400 text-xs flex-wrap">
							<a href="/terms" className="hover:underline">Terms</a>
							<span>•</span>
							<a href="/privacy-policy" className="hover:underline">Privacy</a>
							<span>•</span>
							<button
								type="button"
								className="hover:underline bg-transparent border-0 text-xs text-slate-400 cursor-pointer p-0"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									showConsentSettings();
								}}
							>
								Cookies
							</button>
							<span>•</span>
							<a href="/contact" className="hover:underline">Contact</a>
						</div>
					</div>
				</footer>
			</div>
		);
	}

	return (
		<Layout>
			<Seo title="Pricing — 2homes.app" canonical="https://2homes.app/pricing" robots="index,follow" />
			{content}
		</Layout>
	);
}
